import client from './client';
import type { NodeKind, ArtifactKind } from './processes';
import { PROCESSES_API } from './routes';

/**
 * Wire-format projection of a backend `NodeDefinition`. The catalog endpoint
 * returns one of these per registered node definition (latest version per
 * id, deprecated definitions filtered out for the editor palette).
 *
 * Mirrors `com.aibuild.os.controlplane.process.dto.NodeDefinitionDto` on
 * the backend. New fields land on the wire first, then we add them here.
 */
export interface NodeDefinitionDto {
  readonly id: string;
  readonly version: number;
  readonly kind: NodeKind;
  readonly deprecated: boolean;
  /** True for everything except Note. The editor's "Run this node"
   *  button and the engine both short-circuit on `!isComputable`. */
  readonly isComputable: boolean;
  readonly displayMetadata: DisplayMetadataDto;
  readonly defaultPorts: PortSchemaDto;
  /** Pre-populated config blob that the editor seeds new instances with.
   *  Shape is whatever the backend's typed `NodeConfig` record serialises
   *  to (e.g. `{ valueType: 'TEXT', required: true }` for Input). */
  readonly defaultConfig: unknown;
  readonly pythonAgentId: string | null;
  readonly tags: readonly string[];
  /**
   * Editor body-form schema. The generic `ProcessNode` renderer iterates
   * this list to draw the editable fields per node — adding / renaming /
   * relabelling a field is a pure backend change, no FE component to
   * touch. Empty list = no body fields.
   */
  readonly configFields: readonly ConfigFieldDto[];
  /**
   * Branched-output descriptors. Empty for normal sequential outputs;
   * non-empty for branched kinds (e.g. Gate's Approved / Rejected).
   * Each entry's `portLabel` matches an output port label on
   * `defaultPorts.outputs` so the FE can wire the branch row label to
   * its source handle.
   */
  readonly branches: readonly BranchDto[];
  /**
   * Run-time artifacts panel style. Drives which renderer
   * `ProcessNode` mounts below the body form:
   * - `PREVIEW` → inline thumbnail strip (in-flow nodes, e.g. Task)
   * - `LIST`    → vertical download list (terminal nodes, e.g. Output)
   * - `NONE`    → no panel (decorative kinds, e.g. Note)
   */
  readonly artifactsPanel: ArtifactsPanelStyle;
  /**
   * Per-skill output-kind dropdown options for the Task node, keyed by skill
   * id (plus an empty-string key for the not-yet-selected case). The editor
   * renders the output data-type picker from the union over the node's
   * `skillIds`, so the allow-list lives only on the backend (CAM is narrowed
   * to File / G-code / Text / Number). Absent for every non-Task definition.
   */
  readonly agentOutputKinds?: Readonly<
    Record<string, readonly ConfigFieldOptionDto[]>
  > | null;
  /**
   * Task-node skill dropdown options keyed by execution target
   * (`CLOUD` / `LOCAL`). The editor swaps the `skillIds` field options to
   * match the author's Execution toggle so cloud and local skill catalogs
   * never drift from the runtime allocator. Absent for non-Task definitions.
   */
  readonly agentOptionsByTarget?: Readonly<
    Record<string, readonly ConfigFieldOptionDto[]>
  > | null;
  /**
   * Installed-app requirement per local agent id (e.g. `catia → ["catia"]`).
   * The editor resolves the selected agent's apps to query the capability-
   * coverage endpoint for the assignee hint. Only local agents carry an
   * entry; absent for cloud agents and every non-Task definition.
   */
  readonly agentRequiredApps?: Readonly<Record<string, readonly string[]>> | null;
}

/** Artifacts panel renderer style for a {@link NodeDefinitionDto}. */
export type ArtifactsPanelStyle = 'NONE' | 'PREVIEW' | 'LIST';

/** UI control type for a {@link ConfigFieldDto}. */
export type ConfigFieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'CHECKBOX'
  | 'NUMBER'
  | 'PEOPLE_PICKER'
  | 'GROUP_PICKER';

/**
 * One editable field in a node's body form. {@code key} is the path
 * into the wire {@code data} blob — either a top-level node column
 * ({@code "label"}, {@code "instruction"}) or a typed-config field
 * name ({@code "dataType"}, {@code "reviewerRole"}, {@code "text"}).
 *
 * {@code visibleWhen} is an optional equality predicate the renderer
 * applies to the live config blob: every entry in the map must equal the
 * sibling field's current value for this field to render. Absent or
 * empty means "always visible". Used by the Gate node to swap the
 * `reviewerIds` and `reviewerAgentId` fields based on the
 * `mode` value.
 */
export interface ConfigFieldDto {
  readonly key: string;
  readonly label: string;
  readonly type: ConfigFieldType;
  readonly placeholder: string | null;
  readonly rows: number | null;
  readonly helpText: string | null;
  readonly options: readonly ConfigFieldOptionDto[];
  readonly visibleWhen?: Readonly<Record<string, string>> | null;
  /** {@code MULTI_SELECT} only; null / absent means uncapped. */
  readonly maxSelected?: number | null;
}

/** One choice in a {@code SELECT} or {@code MULTI_SELECT} {@link ConfigFieldDto}. */
export interface ConfigFieldOptionDto {
  readonly value: string;
  readonly label: string;
  /**
   * Optional one-line description shown as a hover tooltip on the option
   * (and on the closed select button once that option is selected). When
   * absent or empty, the renderer skips the tooltip entirely — used today
   * for the Task node's Agent dropdown, opt-in for any future SELECT.
   */
  readonly description?: string | null;
  /** {@code MULTI_SELECT} only: pickable on its own, never beside another. */
  readonly exclusive?: boolean;
}

/**
 * One branched-output slot. {@code portLabel} matches an entry in
 * {@link NodeDefinitionDto.defaultPorts}.outputs so the FE wires the
 * branch row label to the right source handle. {@code tone} drives the
 * branch tint ({@code approved} / {@code rejected} / {@code neutral});
 * {@code iconId} drives the leading glyph via the FE icon registry.
 */
export interface BranchDto {
  readonly portLabel: string;
  readonly tone: string;
  readonly iconId: string;
}

/** Editor-facing metadata for the palette + node card chrome. */
export interface DisplayMetadataDto {
  readonly label: string;
  readonly description: string;
  readonly group: string;
  /** Semantic, framework-agnostic id (e.g. `"input"`, `"render-3d"`)
   *  drawn from the backend's `Icons` constants. The FE looks it up in
   *  `iconRegistry.ts` to map to a React icon component, with a generic
   *  fallback when the id is unknown so a backend deploy that ships a
   *  new id doesn't hard-break the palette. */
  readonly iconId: string;
  readonly tags: readonly string[];
}

/**
 * Fixed + variable port shape declared by the definition. The variable
 * groups are reserved for nodes that allow the user to add / remove
 * inputs / outputs at design time (currently unused but the wire shape is
 * already in place).
 */
export interface PortSchemaDto {
  readonly inputs: readonly PortDefinitionDto[];
  readonly inputGroups: readonly PortGroupDefinitionDto[];
  readonly outputs: readonly PortDefinitionDto[];
  readonly outputGroups: readonly PortGroupDefinitionDto[];
}

export interface PortDefinitionDto {
  readonly label: string;
  /**
   * Canonical single kind for the port. For multi-type ports this is the
   * primary kind (used for chip color, tooltip icon). The full set lives
   * on {@link acceptedKinds}.
   */
  readonly portType: ArtifactKind;
  /**
   * Full set of artifact kinds this port accepts (inputs) or emits
   * (outputs). For single-typed ports this is just `[portType]`.
   * The backend always emits this; older payloads that omit it should
   * fall back to `[portType]` defensively.
   */
  readonly acceptedKinds?: readonly ArtifactKind[];
  readonly required: boolean;
  /**
   * Optional config field key whose value drives this port's runtime
   * type. The BACKEND projects `config[bindsTo]` onto the persisted
   * port_type / accepted_kinds_json on every save; the FE just renders
   * whatever the round-trip detail response carries. Surfaced to the FE
   * for read-only purposes — e.g. tooltip text "type follows the
   * Data type setting" — but no FE save path mutates state based on
   * it. `null` means the port type is fixed by the agent.
   */
  readonly bindsTo?: string | null;
}

export interface PortGroupDefinitionDto {
  readonly groupId: string;
  readonly labelTemplate: string;
  readonly type: ArtifactKind;
  readonly minCount: number;
  readonly maxCount: number;
  readonly defaultCount: number;
}

/**
 * Raw fetcher for the node-definition catalog. The response is the FE's
 * source of truth for what nodes exist, what they look like in the palette,
 * and what their config schema is - there are no FE-side hardcoded
 * definitions outside of the icon string-id -> component mapping in
 * `iconRegistry.ts`.
 *
 * Prefer the {@link useNodeDefinitions} hook for any UI consumer - it
 * shares a process-wide React Query cache that is hard-pinned for the
 * lifetime of the page (catalog is server-static within a deployment, so
 * a backend bump always coincides with a full reload that drops the
 * in-memory cache). Calling this function directly bypasses that cache
 * and is only appropriate for one-off scripts / tests.
 */
export async function listNodeDefinitions(): Promise<readonly NodeDefinitionDto[]> {
  const { data } = await client.get<readonly NodeDefinitionDto[]>(
    `${PROCESSES_API}/node-definitions`,
  );
  return data;
}
